import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer } from '@angular/platform-browser';
import { LayoutService } from '../../_services/layout.service';
import { Subscription } from 'rxjs';
import { ChatbotDialogComponent } from '../../_dialog/chatbot-dialog/chatbot-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ProfileService } from '../../_services/profile.service';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MotifModule } from '@ey-xd/ng-motif';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MsalService } from '@azure/msal-angular';
import { LoanAiService, TenantItem } from '../../loanAiService';
import { LoanApiService } from '../../_services/loan-api.service';

@Component({
  selector: 'app-header',
  imports: [
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    FormsModule,
    MotifModule,
    CommonModule,
  ],
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  readonly dialog = inject(MatDialog);
  private lastTenantId: string | null = null;
  private layoutSubscription: Subscription | undefined;
  private tenantIdSubscription: Subscription | undefined;
  private authSubscription: Subscription | undefined;
  showSideKick: boolean = false;
  @Input() profilePhotoUrl: string = './images/avatar.png';
  @Output() logoutClicked = new EventEmitter<void>();
  profilePicBase64: string = '';
  tenantOptions: any[] = [];
  tenant: string = '';
  client_name: string = '';
  tenantsFull: TenantItem[] = [];
  userInitials: string = '';

  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private layoutService: LayoutService,
    private profileService: ProfileService,
    private msalService: MsalService,
    private location: Location,
    private router: Router,
    private loanApiService: LoanApiService,
    private aiService: LoanAiService
  ) {}

  ngOnInit(): void {
    this.matIconRegistry.addSvgIcon(
      'right_panel_close',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        './images/icons/sidebar-collapse.svg'
      )
    );

    this.layoutSubscription = this.layoutService.showSideKick.subscribe(
      (show) => {
        this.showSideKick = show;
      }
    );
    this.aiService.tenants$.subscribe((tenants: TenantItem[]) => {
      if (tenants && tenants.length > 0) {
        this.tenantsFull = tenants;
        this.tenantOptions = tenants.map((tenant: TenantItem) => ({
          label: tenant.tenant_name,
          value: tenant.tenant_guid,
        }));
        const savedTenantId = localStorage.getItem('tenantId');
        if (savedTenantId) {
          this.tenant = savedTenantId;
          this.onTenantChange(savedTenantId);
        } else if (tenants.length > 0) {
          this.tenant = tenants[0].tenant_guid;
          this.onTenantChange(tenants[0].tenant_guid);
        }
        // set client_name based on selected tenant
        const matched = tenants.find(t => t.tenant_guid === this.tenant);
        if (matched) {
          this.client_name = matched.tenant_name;
        } else if (tenants.length > 0) {
          this.client_name = tenants[0].tenant_name;
        }
      }
    });

    this.tenantIdSubscription = this.aiService.tenantId$.subscribe(
      (tenantId) => {
        // Only navigate to root when tenantId transitions from falsy to truthy.
        // This avoids forcing a redirect on page refresh where tenantId is already set.
        if (tenantId && !this.lastTenantId && this.router.url !== '/') {
          this.router.navigate(['/']);
        }
        this.lastTenantId = tenantId || null;
      }
    );
    const name = this.profileService.getUsername();
    this.userInitials = this.computeInitials(name);

    // update initials reactively when authentication status changes
    this.authSubscription = this.profileService.isAuthenticated$.subscribe(
      (isAuth) => {
        if (isAuth) {
          const updatedName = this.profileService.getUsername();
          this.userInitials = this.computeInitials(updatedName);
        } else {
          // clear initials on logout
          this.userInitials = '';
        }
      }
    );
  }

  computeInitials(fullName?: string | null): string {
    if (!fullName) return '';
    const parts = fullName
      .trim()
      .split(/\s+/)
      .filter((p) => p.length > 0);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    // take first char of first and last parts
    const first = parts[0].charAt(0).toUpperCase();
    const last = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${first}${last}`;
  }

  ngDoCheck(): void {
    if (this.profilePhotoUrl === './images/avatar.png') {
      const sessionPhoto =
        this.profileService.getBlobFromSessionStorage('prpic');
      if (sessionPhoto) {
        this.profilePicBase64 = window.URL.createObjectURL(sessionPhoto);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.layoutSubscription) {
      this.layoutSubscription.unsubscribe();
    }
    if (this.tenantIdSubscription) {
      this.tenantIdSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  showChatbot() {
    this.dialog.open(ChatbotDialogComponent, {
      width: '360px',
      height: '90vh',
      position: { top: '10px', right: '5px' },
      disableClose: true,
      hasBackdrop: false,
    });
    return false;
  }
  getLocation(): string {
    return this.location.path().includes('ruleEngine')
      ? 'Business Service'
      : 'Credit Risk';
  }
  // goToAdmin() {
  //   this.router.navigate(['/ruleEngine']);
  // }

  logout() {
    const token =
      sessionStorage.getItem('token') || localStorage.getItem('token');
    this.loanApiService.logout(token || '').subscribe({
      next: () => {
        this.finalizeLogout();
      },
      error: () => {
        this.finalizeLogout();
      },
    });
  }

  private finalizeLogout(): void {
    sessionStorage.removeItem('token');
    localStorage.removeItem('token');
    this.logoutClicked.emit();
    this.msalService.logoutRedirect();
  }

  onTenantChange(event: any) {
    this.aiService.setTenantId(event);
  }
}
